import type { InferRpcDefinition } from "@redotech/rpc/definition";
import { getCampaignTimelineData } from "./schema/campaign-timeline/get-campaign-timeline-data";
import { claimConversation } from "./schema/conversations/claim-conversation";
import { getConversationById } from "./schema/conversations/get-conversation-by-id";
import { getConversationLogs } from "./schema/conversations/get-conversation-logs";
import { getConversationMessages } from "./schema/conversations/get-conversation-messages";
import { getQueueConversations } from "./schema/conversations/get-queue-conversations";
import { getQueueCount } from "./schema/conversations/get-queue-count";
import { hasDiscountConfigured } from "./schema/conversations/has-discount-configured";
import { releaseConversationClaim } from "./schema/conversations/release-conversation-claim";
import { createMarketingAgentPrompt } from "./schema/marketing-agent-prompt/create-marketing-agent-prompt";
import { getAllMarketingAgentPrompts } from "./schema/marketing-agent-prompt/get-all-marketing-agent-prompts";
import { updateMarketingAgentPrompt } from "./schema/marketing-agent-prompt/update-marketing-agent-prompt";
import { getActiveMarketingAgentSplits } from "./schema/marketing-agent/get-active-marketing-agent-splits";
import { getMarketingAgentChannelDistribution } from "./schema/marketing-agent/get-marketing-agent-channel-distribution";
import { getMarketingAgentDecisionDetails } from "./schema/marketing-agent/get-marketing-agent-decision-details";
import { getMarketingAgentDecisionTrends } from "./schema/marketing-agent/get-marketing-agent-decision-trends";
import { getMarketingAgentDecisions } from "./schema/marketing-agent/get-marketing-agent-decisions";
import { getMarketingAgentKeyMetrics } from "./schema/marketing-agent/get-marketing-agent-key-metrics";
import { getMarketingAgentLlmCostMetrics } from "./schema/marketing-agent/get-marketing-agent-llm-cost-metrics";
import { getMarketingAgentLlmCostTrends } from "./schema/marketing-agent/get-marketing-agent-llm-cost-trends";
import { getMarketingAgentPerformanceMetrics } from "./schema/marketing-agent/get-marketing-agent-performance-metrics";
import { getMarketingAgentTagDistribution } from "./schema/marketing-agent/get-marketing-agent-tag-distribution";
import { getMarketingAgentTriggerEventDistribution } from "./schema/marketing-agent/get-marketing-agent-trigger-event-distribution";
import { getMarketingAgentWriterMessageTrends } from "./schema/marketing-agent/get-marketing-agent-writer-message-trends";
import { escalateToMerchantSupport } from "./schema/messages/escalate-to-merchant-support";
import { fetchDiscountInfo } from "./schema/messages/fetch-discount-info";
import { flagDraftMessage } from "./schema/messages/flag-draft-message";
import { getReplyToEmail } from "./schema/messages/get-reply-to-email";
import { regenerateDraftMessage } from "./schema/messages/regenerate-draft-message";
import {
  adminSendDraftMessage,
  contractorSendDraftMessage,
} from "./schema/messages/send-draft-message";
import {
  adminSendManualMessage,
  contractorSendManualMessage,
} from "./schema/messages/send-manual-message";
import { skipDraftMessage } from "./schema/messages/skip-draft-message";
import { alertRecoverSlack } from "./schema/recover/alert-recover-slack";
import { getRecoverAnalytics } from "./schema/recover/get-recover-analytics";
import { getRecoverMerchantNotes } from "./schema/recover/get-recover-merchant-notes";
import { getRecoverTemplates } from "./schema/recover/get-recover-templates";
import { unsubscribeRecoverForPhoneNumber } from "./schema/recover/unsubscribe-recover-for-phone-number";
import { getSampleTrackingCodes } from "./schema/shipment-tracking/get-sample-tracking-codes";
import { getTeamCarrierDistribution } from "./schema/shipment-tracking/get-team-carrier-distribution";
import { lookupTracking } from "./schema/shipment-tracking/lookup-tracking";
import { testTrackingSamples } from "./schema/shipment-tracking/test-tracking-samples";

type AdminMarketingRpcDefinitionType = {
  createMarketingAgentPrompt: typeof createMarketingAgentPrompt;
  updateMarketingAgentPrompt: typeof updateMarketingAgentPrompt;
  getAllMarketingAgentPrompts: typeof getAllMarketingAgentPrompts;
  getCampaignTimelineData: typeof getCampaignTimelineData;
  claimConversation: typeof claimConversation;
  getConversationById: typeof getConversationById;
  getQueueConversations: typeof getQueueConversations;
  getQueueCount: typeof getQueueCount;
  getConversationLogs: typeof getConversationLogs;
  getConversationMessages: typeof getConversationMessages;
  hasDiscountConfigured: typeof hasDiscountConfigured;
  releaseConversationClaim: typeof releaseConversationClaim;
  escalateToMerchantSupport: typeof escalateToMerchantSupport;
  fetchDiscountInfo: typeof fetchDiscountInfo;
  getReplyToEmail: typeof getReplyToEmail;
  adminSendDraftMessage: typeof adminSendDraftMessage;
  contractorSendDraftMessage: typeof contractorSendDraftMessage;
  adminSendManualMessage: typeof adminSendManualMessage;
  contractorSendManualMessage: typeof contractorSendManualMessage;
  flagDraftMessage: typeof flagDraftMessage;
  skipDraftMessage: typeof skipDraftMessage;
  regenerateDraftMessage: typeof regenerateDraftMessage;
  alertRecoverSlack: typeof alertRecoverSlack;
  getRecoverAnalytics: typeof getRecoverAnalytics;
  getRecoverMerchantNotes: typeof getRecoverMerchantNotes;
  getRecoverTemplates: typeof getRecoverTemplates;
  unsubscribeRecoverForPhoneNumber: typeof unsubscribeRecoverForPhoneNumber;
  getActiveMarketingAgentSplits: typeof getActiveMarketingAgentSplits;
  getMarketingAgentDecisionDetails: typeof getMarketingAgentDecisionDetails;
  getMarketingAgentDecisions: typeof getMarketingAgentDecisions;
  getMarketingAgentKeyMetrics: typeof getMarketingAgentKeyMetrics;
  getMarketingAgentDecisionTrends: typeof getMarketingAgentDecisionTrends;
  getMarketingAgentTagDistribution: typeof getMarketingAgentTagDistribution;
  getMarketingAgentChannelDistribution: typeof getMarketingAgentChannelDistribution;
  getMarketingAgentTriggerEventDistribution: typeof getMarketingAgentTriggerEventDistribution;
  getMarketingAgentWriterMessageTrends: typeof getMarketingAgentWriterMessageTrends;
  getMarketingAgentLlmCostMetrics: typeof getMarketingAgentLlmCostMetrics;
  getMarketingAgentLlmCostTrends: typeof getMarketingAgentLlmCostTrends;
  getMarketingAgentPerformanceMetrics: typeof getMarketingAgentPerformanceMetrics;
  getTeamCarrierDistribution: typeof getTeamCarrierDistribution;
  getSampleTrackingCodes: typeof getSampleTrackingCodes;
  lookupTracking: typeof lookupTracking;
  testTrackingSamples: typeof testTrackingSamples;
};

const marketingAgentPromptSchema = {
  createMarketingAgentPrompt,
  updateMarketingAgentPrompt,
  getAllMarketingAgentPrompts,
} as const;

const campaignTimelineSchema = { getCampaignTimelineData } as const;

const conversationsSchema = {
  claimConversation,
  getConversationById,
  getQueueConversations,
  getQueueCount,
  getConversationLogs,
  getConversationMessages,
  hasDiscountConfigured,
  releaseConversationClaim,
} as const;

const messagesSchema = {
  escalateToMerchantSupport,
  fetchDiscountInfo,
  getReplyToEmail,
  adminSendDraftMessage,
  contractorSendDraftMessage,
  adminSendManualMessage,
  contractorSendManualMessage,
  flagDraftMessage,
  skipDraftMessage,
  regenerateDraftMessage,
} as const;

const recoverSchema = {
  alertRecoverSlack,
  getRecoverAnalytics,
  getRecoverMerchantNotes,
  getRecoverTemplates,
  unsubscribeRecoverForPhoneNumber,
} as const;

const marketingAgentSplitsSchema = {
  getActiveMarketingAgentSplits,
  getMarketingAgentDecisionDetails,
  getMarketingAgentDecisions,
  getMarketingAgentKeyMetrics,
  getMarketingAgentDecisionTrends,
  getMarketingAgentTagDistribution,
  getMarketingAgentChannelDistribution,
  getMarketingAgentTriggerEventDistribution,
  getMarketingAgentWriterMessageTrends,
  getMarketingAgentLlmCostMetrics,
  getMarketingAgentLlmCostTrends,
  getMarketingAgentPerformanceMetrics,
} as const;

const shipmentTrackingSchema = {
  getTeamCarrierDistribution,
  getSampleTrackingCodes,
  lookupTracking,
  testTrackingSamples,
} as const;

export const rpcDefinition: AdminMarketingRpcDefinitionType = {
  ...marketingAgentPromptSchema,
  ...campaignTimelineSchema,
  ...conversationsSchema,
  ...messagesSchema,
  ...recoverSchema,
  ...marketingAgentSplitsSchema,
  ...shipmentTrackingSchema,
};

export type AdminMarketingRpcDefinition = InferRpcDefinition<
  typeof rpcDefinition
>;
